/* eslint react/prop-types: 0 */
import ELEMENT, { createTestId } from 'common/element';
import { KubeCluster, KubeController } from 'common/graphqlTypes';
import Link from 'components/common/Link/Link';
import { createOrchestrationLink } from './LogOverviewPage.functions';

import './LogOverviewSourcesRow.scss';
import './LogOverviewClusterTableRow.scss';

type LogOverviewClusterTableRowProps = {
  cluster: KubeCluster;
  controller: KubeController;
  hideDashboardColumn: boolean;
  clusterIndex: number;
  controllerIndex: number;
};

/**
 * This component creates an individual row for a cluster. By default we show only four controllers per cluster but the user
 * has the ability to click "show more" to see the remainder.
 *
 * @prop cluster                cluster data
 * @prop controller             controller data
 * @prop hideDashboardColumn    if true, omit dashboard names
 * @prop clusterIndex           index of cluster within containing list
 * @prop controllerIndex        index of controller within containing cluster
 */
function LogOverviewClusterTableRow({
  cluster,
  controller,
  hideDashboardColumn,
  clusterIndex,
  controllerIndex,
}: LogOverviewClusterTableRowProps): JSX.Element {
  // Create a link to the dashboard page.
  const renderDashboardsLink = (): JSX.Element | null => {
    if (cluster.dashboard?.name) {
      const dashboardLink = `/dash?page=${cluster.dashboard.name}&param_cluster=${encodeURIComponent(
        cluster.name,
      )}&param_controller=${encodeURIComponent(controller.name)}`;
      const dashboardTestId = createTestId(ELEMENT.overviewControllerDashboard, clusterIndex, controllerIndex);
      return (
        <Link href={dashboardLink} testId={dashboardTestId}>
          {cluster.dashboard.name}
        </Link>
      );
    }

    return null;
  };

  // Get a list of labels where each label is a link.
  const renderLabels = (): JSX.Element[] | null => {
    let parsedLabels = controller.labels && controller.labels.split(',');
    if (parsedLabels?.length > 0) {
      parsedLabels = parsedLabels as string[];
      return parsedLabels.map((label: string, labelIndex: number) => {
        const [labelKey, labelValue] = label.split('=');
        const escapedLabelKey = labelKey.replace(/([^a-z0-9_-])/gi, '\\$1');
        const labelLinkStr = `$${escapedLabelKey}='${labelValue}'`;
        const labelTestId = createTestId(ELEMENT.overviewControllerLabel, clusterIndex, controllerIndex, labelIndex);

        return (
          <Link
            key={labelLinkStr}
            className="LogOverviewClusterTableRow__LabelLink"
            href={createOrchestrationLink(labelLinkStr, cluster.name, null, null)}
            testId={labelTestId}
          >
            {label}
          </Link>
        );
      });
    }
    return null;
  };

  const podCounter = controller.pods.length > 0 ? <span>{'(' + controller.pods.length + ')'}</span> : null;

  const controllerLink = createOrchestrationLink('', cluster.name, controller.name, controller.kind);
  const controllerTestId = createTestId(ELEMENT.overviewController, clusterIndex, controllerIndex);

  const allLogsLink = createOrchestrationLink('', cluster.name, controller.name, controller.kind);
  const allLogsTestId = createTestId(ELEMENT.overviewAllLogs, clusterIndex, controllerIndex);

  return (
    <tr className="LogOverviewClusterTableRow">
      <td>
        <Link
          className="LogOverviewClusterTableRow__ControllerNameLink"
          href={controllerLink}
          testId={controllerTestId}
        >
          {controller.name}
        </Link>
        <span className="LogOverviewClusterTableRow__PodCounter">{podCounter}</span>
      </td>
      <td>
        <Link href={allLogsLink} testId={allLogsTestId}>
          all logs
        </Link>
      </td>
      {hideDashboardColumn ? null : <td>{renderDashboardsLink()}</td>}
      <td>{renderLabels()}</td>
    </tr>
  );
}

export default LogOverviewClusterTableRow;
